import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { apiClient } from '@/api/client'
import { API_BASE_URL } from '@/api/config'

// Must match admin.auth.cognito.app-callback-url and the scheme registered in Info.plist / AndroidManifest.xml.
const APP_CALLBACK_URL = 'leckerorder-admin://auth/cognito'
const VERIFIER_KEY = 'cognitoAppVerifier'

/** The iOS/Android app cannot follow the Cognito redirects in its WebView, so it logs in through an in-app browser. */
export const usesNativeCognitoLogin = ['ios', 'android'].includes(Capacitor.getPlatform())

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function startNativeCognitoLogin(): Promise<void> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)))
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  // sessionStorage survives a live-update reload while the browser sheet is open.
  sessionStorage.setItem(VERIFIER_KEY, verifier)
  await Browser.open({ url: `${API_BASE_URL}/auth/cognito/login?appChallenge=${base64Url(new Uint8Array(digest))}` })
}

/**
 * Turns the backend's redirect to leckerorder-admin://auth/cognito into this app's session.
 * `onResult` receives true once the session cookie is set, false when the login failed.
 */
export function listenForNativeCognitoLogin(onResult: (success: boolean) => void): () => void {
  const listener = App.addListener('appUrlOpen', ({ url }) => {
    if (!url.startsWith(APP_CALLBACK_URL)) return
    // Android brings the app back over the Custom Tab by itself; close() is iOS-only.
    Browser.close().catch(() => { /* already closed */ })
    const code = new URL(url).searchParams.get('code')
    const verifier = sessionStorage.getItem(VERIFIER_KEY)
    sessionStorage.removeItem(VERIFIER_KEY)
    if (!code || !verifier) {
      onResult(false)
      return
    }
    apiClient
      .post('/api/admin/auth/cognito/app-session', { code, verifier }, { validateStatus: (status) => status < 500 })
      .then((response) => onResult(response.status === 200))
      .catch(() => onResult(false))
  })
  return () => void listener.then((handle) => handle.remove())
}
